import { approverRoleLabel, deliveredOrderPrompt, isOrderComplete } from './statuses';

// Shared row-hover tint — the SINGLE source of truth for the hover background on
// clickable list/table rows across the Planning pages (Gag Breakdown gag rows,
// Manage Teams team rows, My Tasks task/gag rows) so the three can't drift apart.
//
// Matches the Breakdown gag-row convention exactly: gray-50 in light mode, and a
// gray-700/30 opacity fill in dark mode. NB: index.css carries an
// `html.dark .hover\:bg-gray-50:hover { background-color:#334155 !important }`
// override, so in dark mode this string actually resolves to slate-700 (#334155)
// via that rule — which is precisely why every row must use the IDENTICAL string:
// same classes in, same (overridden) render out, in both light and dark.
export const ROW_HOVER = 'hover:bg-gray-50 dark:hover:bg-gray-700/30';

// ═══════════════════════════════════════════════════════════════════════════
// FROZEN (sticky-left) CELLS — the row background, RESTATED ON THE CELL
// ═══════════════════════════════════════════════════════════════════════════
//
// 🚨 **A STICKY `<td>` DOES NOT INHERIT ITS `<tr>`'s BACKGROUND.** The `<tr>` is
// painted where it always was; the cell is lifted out and pinned, and it takes
// no fill with it. So a frozen column is TRANSPARENT by default and the rows
// underneath scroll visibly *through* it — the single thing that makes freezing
// a column harder than declaring `position: sticky`. This is the same fact that
// forces every sticky `<thead>` in this app to restate its own fill on the cell
// (`constants/stickyTable.js`), and it is the reason a sticky-right Actions
// column was weighed and deferred on the Breakdown rather than built.
//
// 🔑 **WHY THESE LIVE HERE, BESIDE `ROW_HOVER`, AND NOT BESIDE THE STICKY
// TREATMENT.** The frozen cell has to render the same colour the ROW renders,
// in every state and both themes. A copy of that colour kept anywhere else
// drifts the first time the hover tint is retuned, and the symptom is a stripe
// of the wrong colour under a scrolled row — visible only in a browser, at one
// scroll offset, in one theme. Keeping the mirror adjacent to the thing it
// mirrors is the cheapest guard there is.
//
// 🪤 **THE DARK VALUES ARE NOT THE SAME TAILWIND CLASSES THE ROW USES, AND THAT
// IS FORCED BY THE CASCADE — copying `ROW_HOVER`'s string verbatim is the
// obvious move and it produces the WRONG COLOUR.** `client/src/index.css`
// carries a table of global dark-mode overrides that fire on the BARE class
// name, with `!important`:
//
//     html.dark .bg-white          { background-color:#1e293b !important }   (:103)
//     html.dark .bg-gray-50        { background-color:#1e293b !important }   (:94)
//     html.dark .hover\:bg-gray-50:hover { background-color:#334155 !important } (:339)
//
// So the ROW's dark hover is **#334155 (slate-700)** — delivered by that
// override, NOT by `ROW_HOVER`'s own `dark:hover:bg-gray-700/30`, which the
// `!important` beats outright. The frozen cell's rules are written as ARBITRARY
// VARIANTS, whose compiled class names (`.\[\&\>td\.sfx-frozen-cell\]\:bg-white`
// …) are different strings, so **none of those overrides reach them** and each
// dark value has to be stated explicitly as the colour the override produces:
// `slate-800` (#1e293b) for the resting card fill, `slate-700` (#334155) for the
// hover. Measured against the rendered row, not reasoned from the class names.
//
// 🚨 **AND THE RULES MUST SIT ON THE `<tr>`, NEVER ON THE `<td>` AS PLAIN
// CLASSES.** A plain `bg-white` on the cell picks up the `!important` override
// above and then beats the EXPANDED row's `dark:[&>td]:bg-slate-700`, so an
// expanded row's frozen cells would paint the resting card colour while the rest
// of the row is highlighted — in dark mode only. The arbitrary-variant form has
// no such problem and is also the idiom the expanded-row highlight already uses
// (`[&>td]:bg-gray-100`), for the same reason.
//
// 🔴 **APPLY THIS ONLY ON THE NON-EXPANDED BRANCH.** An expanded row's
// `[&>td]:bg-…` / `[&:hover>td]:bg-…` pair already covers EVERY cell, frozen
// ones included, so the expanded and expanded-hover states need nothing here.
// Applying both would put two conflicting background utilities on one element,
// which resolve by STYLESHEET order rather than by the order they are written —
// the `mb-3 mb-2` hazard this repo already records.
//
// ⚠️ jsdom applies no stylesheet and has no layout, so **nothing under
// `client/src` can show that a frozen cell is opaque, or that its colour matches
// the row's.** Tests pin the strings; the colours are a BROWSER check.

/** The marker class a frozen cell carries, so the `<tr>` rules can select it. */
export const FROZEN_CELL_CLASS = 'sfx-frozen-cell';

/**
 * Row-level background rules for the frozen cells of a NON-EXPANDED row:
 * the resting card fill, plus the hover tint mirroring `ROW_HOVER` above.
 *
 * 🎞️ The cell also carries `transition-colors`, the SAME fade the `<tr>` has
 * (owner 2026-10-14: the first column looked "out of sync" on hover, like a
 * screen tear). Without it the frozen cell's fill SNAPS while the rest of the
 * row FADES over 150ms. Non-expanded branch only, like the fills: an expanded
 * row's `[&>td]` fill has no fade on ANY cell, so all cells change together.
 */
export const ROW_BG_FROZEN_CELLS =
  '[&>td.sfx-frozen-cell]:transition-colors ' +
  '[&>td.sfx-frozen-cell]:bg-white dark:[&>td.sfx-frozen-cell]:bg-slate-800 ' +
  '[&:hover>td.sfx-frozen-cell]:bg-gray-50 dark:[&:hover>td.sfx-frozen-cell]:bg-slate-700';

// ── The "you can action this one" signal on an order row ─────────────────────
//
// It is the EXPAND CHEVRON that turns green, in BOTH order-list layouts.
//
// Product owner, 2026-08-08: *"The green border on items awaiting attention is
// too glaring. I think it would be better to make the notification more subtle.
// Can we try changing the colour of the expand arrow to green for items
// requiring attention"*. ⚠️ **This is a "try it" change.** The shade, and the
// choice of the chevron as the carrier at all, are both his to revise — one
// `git revert` per layout, which is why it landed as two commits.
//
// ⚠️ WHAT WENT, SO NOBODY "RESTORES" IT AS A REGRESSION. Two separate green
// outlines were deleted, one per layout, and they were the SAME predicate drawn
// two different ways:
//   - the TABLE row's `ACTIONABLE_ROW_OUTLINE` — a 1px rectangle drawn
//     edge-selectively on the `<td>`s, which itself replaced a per-cell `ring`
//     on 2026-08-07 after he asked for *"only … the outside border of todays
//     rows in green rather than each cell"*;
//   - the CARD's inline `ring-2 ring-green-300 dark:ring-green-600`, passed as
//     `cardClassName` from `DepartmentOrders.js`.
// (Naming those two tokens in prose here costs nothing even though Tailwind
// scans comments: both utilities are still live on `DepartmentOrders.js`'s
// Export button and on three dashboards' `hover:ring-green-300`, and the built
// stylesheet hash was unchanged by their removal from the card. Do not read
// this as licence to write dead tokens in comments generally.)
// The long note that used to live here — the `[&>td]` edge-selective drawing,
// `border-collapse` merging the segments, and the emit-order interaction with
// the expanded row's blue `border-t-2` — described a collision that can no
// longer happen, so it went with the constant rather than being left to rot.
//
// 🔑 IT IS NOT DATE-BASED, despite the 2026-08-07 wording. `DepartmentOrders.js`
// applies it when `canActionOrder(order)` (i.e. `order.can_action === true`),
// which the same page already labels in green as "· N actionable".
// **If that predicate ever moves, that is a product change, not a refactor.**
//
// ── Why green-700 / green-400, and NOT the outline's green-300 / green-600 ──
// The nearest existing convention for THIS EXACT MEANING is on the same page:
// `DepartmentOrders.js` renders *"· N actionable"* in
// `text-green-700 dark:text-green-400`. The chevron is the per-row form of that
// same count, so it takes the same pair — one meaning, one colour.
//
// The old shades deliberately did NOT come along. green-300 (#86efac) is fine
// as a 1px line across a 1310px row and near-invisible as a 16px glyph — about
// 1.5:1 on white, failing WCAG 1.4.11's 3:1 floor for a graphical object
// outright. A smaller target needs MORE contrast, not the same. Measured, for
// the pair actually used, against every background these chevrons really get:
//
//   text-green-700 (#15803d) on white                     5.02:1
//   text-green-700 (#15803d) on gray-50  (row/card hover)  4.84:1
//   text-green-700 (#15803d) on gray-100 (expanded table)  4.56:1
//   dark:text-green-400 (#4ade80) on gray-800              8.42:1
//   dark:text-green-400 (#4ade80) on slate-700 (expanded)  5.94:1
//
// Worst case 4.56:1 — clear of the 4.5:1 AA text floor, never mind the 3:1
// graphics one. **If he asks for something paler, re-run these numbers first.**
//
// ── ♿ COLOUR IS NOT THE ONLY SIGNAL (WCAG 1.4.1), AND IT CANNOT BE ──────────
// Neither layout carries a text label meaning "yours to action", and the
// obvious candidate does not cover it: `getNextStepInfo`'s "Your Approval
// Needed" fires only for a SUBMITTED order whose bound approver is the viewer,
// whereas `can_action` is also true for a buyer's approved / ordered /
// partially-delivered rows — which read "Approved · Ready to Order" and so on,
// a restatement of status, not "this one is yours". And the table layout is
// passed no next-step node at all. So the meaning is carried twice more, from
// the SAME exported string so the three can never drift — the identical device
// `components/InactiveBadge.js` uses for the deactivated-user treatment:
//
//   - `title` — a hover tooltip, invisible until wanted. Subtlety was the whole
//     request, so nothing may be added that is visible at rest.
//   - a visually-hidden `sr-only` copy — `title` on an element with no ARIA role
//     is announced inconsistently across screen readers, so it cannot be the
//     only non-visual channel. `sr-only` is announced everywhere.
//
// 🪤 That hidden copy IS in the DOM, so it is in the row's `textContent`. A test
// matching row text on an ACTIONABLE order will see this sentence appended;
// non-actionable rows are untouched.
//
// ── 🪤 GREEN MUST SURVIVE HOVER, AND A GLOBAL `!important` TOOK IT ──────────
// Found in a browser by the product owner on 2026-08-08, hours after the shade
// above shipped: *"I see green chevrons but when I hover my mouse over the
// entire table all chevrons turn blue"*. Nothing here was wrong — `index.css`
// carried a deliberately broad rule painting every `[data-chevron]` within four
// descendant levels of ANY hovered element blue, with `!important`, so it beat
// the class this file exports outright. `:hover` matches every ANCESTOR, so
// pointing anywhere in the list matched every chevron in it.
//
// It is fixed in `client/src/index.css` by excluding `:not([data-actionable])`
// on all eight selectors (four levels × light/dark), using the attribute both
// layouts already emit. Two things to carry away:
//   1. **A semantic colour needs its cascade checked, not just its contrast.**
//      The measurements above are necessary and were not sufficient — the class
//      was correct and was never applied. This repo has done the same thing
//      before ("an inert dark-mode class (a global `!important` beat it)").
//      grep `index.css` for `!important` on the property you are setting.
//   2. An actionable chevron gets **NO hover recolour at all** — a deliberate
//      choice, not an omission. The row already answers "you are pointing at
//      me" with ROW_HOVER above, and a darker green would be a second shade
//      needing its own 1.4.11 numbers against the hover backgrounds. Revisiting
//      that is a product decision; re-run the table above if you do.
// Pinned by `client/src/index.chevronHover.test.js`, which asserts the CSS
// SOURCE — jsdom applies no stylesheet, so no test here can show a colour.
//
// ── The chevron also rotates, and green must not read as "expanded" ─────────
// Rotation is geometry (`rotate-90`), actionable is hue; they are orthogonal by
// construction and all four combinations are pinned in
// `client/src/pages/DepartmentOrders.actionableChevron.test.js` §3. The open row
// keeps its own far louder markers besides — the cell tint plus a blue 2px top
// border in the table, the expanded panel in the card — so "open" is never
// signalled by chevron colour alone.

/**
 * `chevronTitle(order)` — WHAT THE EXPAND CHEVRON SAYS ON HOVER, and the same
 * string its `sr-only` copy announces. Returns `string | null`.
 *
 * 🔀 REPLACED `ACTIONABLE_CHEVRON_TITLE = 'Awaiting your action'` on
 * 2026-09-11. That was ONE string on every green chevron, and owner ruling A
 * had just made a manager's green rows THREE different things: `submitted` (the
 * viewer's own pending approval step) plus `approved` / `ordered` /
 * `partially_delivered`, which are orders to PLACE and CHASE and are nobody's
 * approval step. One sentence could not describe all four.
 *
 * 🚨 AND IT ASSIGNED THE READER A JOB. A supervisor whose buyer places
 * the orders read *"your action"* on rows that were the buyer's to progress.
 * Owner: it "should say what the action is" without telling the reader it is
 * theirs. Hence THIRD PERSON throughout — the impersonal phrasing is his, not a
 * style preference.
 *
 * 🔀 REFINED 2026-09-12, AND THE `submitted` SENTENCE IS NOW `Your approval
 * needed` — A NARROWING OF THE RULE ABOVE, NOT A REVERSAL. Owner: *"on Dept
 * Orders we have green chevrons for approvals that are for that user…  please
 * change to 'Your Approval Needed' ONLY if its waiting on that user viewing the
 * tool tip. NOT if its a general buyer/supervisor action."*
 *
 * 🔑 WHY "YOUR" IS ALWAYS TRUE ON A GREEN `submitted` ROW, AND ONLY THERE.
 * The 09-11 objection was that ONE string covered all four green statuses, so a
 * buyer's fulfilment rows inherited a sentence about approval. Ruling A, the
 * same day, narrowed the SUBMITTED green arm to *the viewer's own pending step*:
 * the department list writes `can_action` for a submitted order as
 * `overall_status === 'pending' && approver_user_id === req.userId` (grep the
 * `can_action =` assignment in `routes/orders.js`'s `GET /department` block),
 * which is byte-for-byte the predicate `GET /api/approvals/pending` selects on
 * (grep `oas.approver_user_id = ?` in `routes/approvals.js`). A green submitted
 * chevron therefore means the SAME order is sitting in that viewer's My
 * Approvals queue — it is waiting on them personally, always.
 *
 * 🔴 THE OTHER THREE STAY IMPERSONAL AND THAT IS THE OWNER'S "NOT" CLAUSE.
 * `approved` / `ordered` / `partially_delivered` are the general
 * buyer/supervisor fulfilment actions — orders to PLACE and CHASE, nobody's
 * approval step, and `can_action` there is granted by ROLE (the buyer block) or
 * by CAPABILITY (Stores), never by a step naming the reader. Putting "Your" on
 * any of them reinstates the 09-11 defect exactly. Pinned by §1.7 of
 * `rowStyles.chevronTitle.test.js`, which asserts the three carry no "your" at
 * all rather than asserting three literals a rewording would quietly drift past.
 *
 * 🪤 SENTENCE CASE, `Your approval needed`. Every sibling in the map is
 * sentence case and this one joins them. ⚠️ `getNextStepInfo`'s BADGE label
 * (`utils/orderStepInfo.js`) is Title Case `Your Approval Needed` and fires on
 * the same predicate, so the two renderings of one phrase now differ in case on
 * the same row. Recorded, not resolved — it is the owner's call which way the
 * pair goes, and he typed Title Case in chat.
 *
 * 🔑 THE NON-GREEN ARM IS NEW CAPABILITY, NOT A REWORDING. Owner: *"we do
 * actually know WHO we are waiting on"*. A non-green `submitted` row carries
 * `approval_info`, so its chevron now names the awaited approver — where today
 * it renders no `title` attribute at all. Every OTHER non-green status stays
 * attribute-less.
 *
 * 🪤 `null`, NEVER `''`. Both call sites spread the result straight into
 * `title=`, and `title=""` renders an EMPTY popup box on hover — worse than no
 * tooltip. Root CLAUDE.md records the same rule for `dueIndicatorTitle`.
 *
 * 🪤 THE GREEN DEFAULT IS LOAD-BEARING. `can_action` is computed
 * server-side and its reasons have changed twice this month (Stores holds it on
 * the goods-in statuses; ruling A narrowed the submitted arm). A green row at an
 * unanticipated status must fall back to the neutral `Actionable` rather than
 * borrowing one of the four specific sentences — a confident wrong statement
 * about an order is worse than a vague true one. Pinned by
 * `rowStyles.chevronTitle.test.js` §1.5/§1.6.
 *
 * 🔴 THE ROLE LABEL IS SHARED, THE PUNCTUATION IS NOT. The map and its
 * fallback chain come from `constants/statuses.js#approverRoleLabel`, the same
 * one `utils/orderStepInfo.js#getNextStepInfo` uses — so the two can never
 * disagree about what a role is CALLED. The shapes differ on purpose: a full
 * sentence in the step panel (*"Waiting for approval from Jake Dimmer
 * Supervisor"*), parentheses in a hover tooltip on a 40px target. Recorded here
 * so it never later reads as drift.
 *
 * 🔑 NOTHING HERE TOUCHES COLOUR OR ROTATION. `ACTIONABLE_CHEVRON_CLASS`,
 * the measured contrast, the no-hover-recolour decision and the
 * rotation-is-orthogonal-to-hue rule above are all unchanged.
 *
 * @param {{ status?: string, can_action?: boolean, approval_info?: object }|null|undefined} order
 * @returns {string|null}
 */
export function chevronTitle(order) {
  if (!order) return null;

  // 🔑 COMPLETE IS TERMINAL AND WINS OVER EVERYTHING (2026-09-11). Nothing is
  // pending, so no other sentence is true — a manager's GREEN
  // `partially_delivered` row that has been marked complete reads `Complete`,
  // not `Part delivered, items outstanding`. The ✓ Complete badge sits beside
  // it in the same status group and the two must not disagree.
  // Read through the SHARED `isOrderComplete`, never a second test of
  // `completed_at` — it arrives as an ISO datetime STRING.
  if (isOrderComplete(order)) return 'Complete';

  // 🪤 `=== true`, never truthiness — the house rule for this flag, and not
  // hypothetical: a missed `await` on the server serialises a Promise to `{}`,
  // which is truthy, and `canActionOrder` tests `=== true` everywhere. A green
  // sentence on a row the server never marked actionable is exactly the
  // confident-wrong-statement failure the default above exists to avoid.
  if (order.can_action === true && ACTIONABLE_CHEVRON_TITLES[order.status]) {
    return ACTIONABLE_CHEVRON_TITLES[order.status];
  }

  // 🔀 THE DELIVERED ARM (2026-09-11). Owner: *"some chevrons have no tool
  // tip like delivered, no paperwork. this should have a tool tip saying
  // something like order requires marking as complete and upload delivery
  // paperwork if required"*. A delivered row that nobody has marked complete
  // was one of the states this helper still said nothing about.
  //
  // 🔑 TWO CLAUSES THAT FAIL DIFFERENTLY, AND THE ASYMMETRY IS THE DESIGN.
  // The COMPLETION clause is justified by the STATUS ALONE, so it never fails
  // quiet. The PAPERWORK clause is an extra and does: it is appended only on an
  // explicit `has_delivery_paperwork === false`.
  //
  // 🪤 `deliveredOrderPrompt` CANNOT GATE THIS ARM — it answers `null` for
  // BOTH "complete" and "paperwork flag missing", so gating on it would say
  // NOTHING on a flag-missing row, which is the opposite of the rule above. It
  // is used only for the question it really answers. Pinned by §5.3.
  //
  // 🔴 IT SITS BEFORE THE NEUTRAL `Actionable` DEFAULT, DELIBERATELY, even
  // though a GREEN delivered row is UNREACHABLE today — measured by enumerating
  // the three writers of `can_action` in `routes/orders.js`'s `GET /department`
  // block, none of which admits `delivered`. If the server ever widens one, the
  // informative sentence wins automatically instead of silently degrading to
  // one word. The four SPECIFIC green sentences still come first; none of their
  // statuses can be `delivered`, so they never compete. Pinned by §5.6.
  if (order.status === 'delivered') {
    return deliveredOrderPrompt(order) === 'no_paperwork'
      ? 'Delivered, needs marking as complete; upload delivery paperwork if required'
      : 'Delivered, needs marking as complete';
  }

  if (order.can_action === true) return 'Actionable';

  // Not green. The one thing we can still say is WHO the order is waiting on,
  // and only a `submitted` row carries that.
  if (order.status !== 'submitted') return null;
  const info = order.approval_info;
  if (!info) return null;

  // 🚨 A NULL `approver_user_id` BINDS NOBODY — never print the name beside
  // it. The row still CARRIES an `approver_name`, and the server sets the id
  // NULL precisely when a workflow template's name matched two people. Printing
  // the name would assert that a specific person owes this order when the
  // server has explicitly declined to say so. Same rule `canPokeOrder` and
  // `isApprovingSomeoneElsesStep` follow, compared by id and never by name.
  return awaitingApproverLabel(info);
}

/**
 * "Awaiting Jake Dimmer (Buyer)" — or plain "Awaiting approval" when the step
 * binds nobody. THE one wording for *who this order is waiting on*.
 *
 * 🔀 **EXTRACTED FROM `chevronTitle` ON 2026-09-13** so Department Orders'
 * `Skip remaining approvals…` dialog can name the same person in the same
 * words. `chevronTitle` itself cannot serve that caller: it answers an
 * `can_action: true` row with `'Actionable'` LONG before it reaches the
 * approver, and a submitted row a manager may skip is very often actionable —
 * so calling it from the dialog would print "Actionable" where a name belongs.
 * Copying the two lines instead would have put this repo's most-repaired class
 * (a name treated as an identity) in two places at once.
 *
 * 🚨 A NULL `approver_user_id` BINDS NOBODY — never print the name beside it.
 * The row still CARRIES an `approver_name`, and the server sets the id NULL
 * precisely when a workflow template's name matched two people. Printing the
 * name would assert that a specific person owes this order when the server has
 * explicitly declined to say so. Same rule `canPokeOrder` and
 * `isApprovingSomeoneElsesStep` follow, compared by id and never by name.
 *
 * 🔑 IMPERSONAL, per the 2026-09-11 ruling on the chevron tooltips — it says
 * who the order waits on, never "your" anything.
 *
 * @param {{approver_user_id?: number|null, approver_name?: string|null, approver_role?: string|null}|null|undefined} info
 *        an order's `approval_info`.
 * @returns {string}
 */
export function awaitingApproverLabel(info) {
  if (!info || info.approver_user_id == null || !info.approver_name) return 'Awaiting approval';
  return `Awaiting ${info.approver_name} (${approverRoleLabel(info.approver_role)})`;
}

/**
 * The green sentences, by status. Not exported: `chevronTitle` is the only
 * correct way to ask, because the default and the non-green arms are part of
 * the answer. A caller reading this map directly would reintroduce exactly the
 * "one sentence for every state" defect the helper replaced.
 */
const ACTIONABLE_CHEVRON_TITLES = {
  // 🔴 THE ONE PERSONAL SENTENCE IN THIS MAP (owner 2026-09-12). A green
  // `submitted` row is the viewer's OWN pending approval step — same predicate
  // as their My Approvals queue; see the docblock above. The three below are
  // the general buyer/supervisor fulfilment actions and must never say "your".
  submitted: 'Your approval needed',
  approved: 'Approved, not yet placed',
  ordered: 'Ordered, awaiting delivery',
  partially_delivered: 'Part delivered, items outstanding',
};

/**
 * The green pair for an actionable row's expand chevron. Exported so both
 * layouts render the SAME string and the tests assert the string the component
 * renders rather than a transcription of it — a transcribed class name is a
 * second source of truth that goes stale silently.
 */
export const ACTIONABLE_CHEVRON_CLASS = 'text-green-700 dark:text-green-400';

/** The chevron's default colour, when the row is not the viewer's to action. */
export const DEFAULT_CHEVRON_CLASS = 'text-gray-400';

// ── The PO / order-number TEXT on a COMPLETED order (2026-08-16) ────────────
//
// Product owner, verbatim: *"lets change the colour of the PO and order
// numbers to the same chevron green on completed orders please."*
//
// 🔑 **DELIBERATELY THE SAME GREEN VALUE AS `ACTIONABLE_CHEVRON_CLASS`, AND
// DELIBERATELY A SEPARATE CONSTANT — this is the owner's explicit colour
// choice, not a reuse of the actionable meaning.** "Actionable" (yours to
// action) and "complete" (administratively closed out) are unrelated
// predicates that happen to share a shade today; importing
// `ACTIONABLE_CHEVRON_CLASS` by that name at a completed-order call site
// would read as "this text is actionable", which is false, and would couple
// the two meanings so retuning one shade retunes the other by accident. One
// definition, two names, the SAME string — so the two CAN diverge the moment
// either meaning needs its own shade, without a search-and-replace.
//
// ♿ **Text, not a graphical object, so the WCAG 1.4.1/1.4.3 question is
// different from the chevron's.** The PO number and order number are TEXT at
// `text-xs font-semibold` — `text-green-700` clears the 4.5:1 AA text floor
// against every background these cells sit on (see the chevron's own
// measurements above; the PO/order-number cell shares the same row
// backgrounds). WCAG 1.4.11's 3:1 NON-TEXT floor does not apply to text at
// all, and even the non-text question would be moot here: the colour is
// REDUNDANT with the ✓ Complete badge that already sits on the same row (both
// layouts, `isOrderComplete`-gated), never the SOLE carrier of "this order is
// complete" — so 1.4.1 (colour is not the only signal) is satisfied by the
// badge's text, independent of this class.
export const COMPLETED_ORDER_NUMBER_CLASS = 'text-green-700 dark:text-green-400';

// ── The collapsed-preview ITEM NAME on a RECEIVED line (2026-08-16) ─────────
//
// Product owner, verbatim: *"change the text colour of the collapsed item NAME
// to the same green as the chevron when the item has been ticked as
// received. Do this on my orders and dep orders. Should share the same code."*
//
// 🔑 **A THIRD, SEPARATE CONSTANT carrying the SAME green value as
// `ACTIONABLE_CHEVRON_CLASS` / `COMPLETED_ORDER_NUMBER_CLASS`, for the same
// reason those two are separate from each other**: "actionable", "complete"
// and "received" are three unrelated predicates that happen to share a shade
// today. Importing either sibling constant at this call site would name the
// wrong meaning and couple three shades that may need to diverge independently.
//
// ♿ **Text, not a graphical object** — same WCAG question as
// `COMPLETED_ORDER_NUMBER_CLASS`, and the contrast is already measured for
// this exact green against the row/expanded backgrounds the item-preview grid
// sits on (see the chevron's own measurements above): 4.56–5.02:1 light,
// 5.94–8.42:1 dark — worst case clears the 4.5:1 AA text floor.
export const RECEIVED_ITEM_NAME_CLASS = 'text-green-700 dark:text-green-400';

